import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { AuthState, EmployeeData, LoginRequest, LoginResponse, EmployeeRole } from '../models/types';
import { userService } from '../services/api/userService';

interface AuthStore extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  setUser: (user: EmployeeData) => void;
  setToken: (token: string) => void;
  setSelectedRole: (role: EmployeeRole) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthStore>()(



  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      selectedRole: null,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        try {
          const response: LoginResponse = await userService.login(credentials);

          if (response.success && response.data) {
            const userRoles = response.data.employeeData?.role as EmployeeRole || [];
            const userData = JSON.stringify(response.data.employeeData || []);
            const isRoleValid = userRoles.includes(credentials.selectedRole);
            if (!isRoleValid) {
              throw new Error("Invalid credentials or role mismatch.");
            }
            sessionStorage.setItem('authToken', response.data.token);
            sessionStorage.setItem('LoggedInUser', userData);

            set({
              user: response.data.employeeData,
              token: response.data.token,
              isAuthenticated: true,
              isLoading: false,
              selectedRole: credentials.selectedRole || null,
            });
          } else {
            throw new Error(response.message || 'Login failed');
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await userService.logout();
        } catch (error) {
          console.log('Logout error:', error);
        } finally {
          sessionStorage.removeItem('authToken');
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            selectedRole: null,
          });
        }
      },

      getCurrentUser: async () => {
        set({ isLoading: true });
        try {
          const currentUser = sessionStorage.getItem("LoggedInUser");
          if (currentUser) {
            let UserData = JSON.parse(currentUser);
            set({
              user: UserData,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      setUser: (user: EmployeeData) => {
        set({ user });
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: true });
      },

      setSelectedRole: (role: EmployeeRole) => {
        set({ selectedRole: role });
      },

      clearAuth: () => {
        sessionStorage.removeItem('authToken');

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          selectedRole: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        selectedRole: state.selectedRole,
      }),
    }
  )
); 